import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useReduxAuth';
import { useAccessibleFirmIds } from './useAccessibleFirmIds';
import { hasuraClient } from '../utils/graphqlClient';
import { GET_ALERT_INSTANCES, GET_ALERT_CONFIGS, GET_MY_DISMISSALS } from '../graphql/queries/alerts';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const filterInstances = (instances, configs, user, firmIds) => {
  if (user?.role === 'ROOT') return instances;

  const userId = parseInt(user?.id);
  const teamId = user?.team_id ? parseInt(user.team_id) : null;

  return instances.filter(inst => {
    const cfg = configs.find(c => c.alert_type_code === inst.alert_type_code);
    if (!cfg) return false;
    const targets = cfg.alert_config_targets || [];

    if (targets.length > 0) {
      const inTargets = targets.some(t => {
        if (t.target_type === 'user') return parseInt(t.target_value) === userId;
        if (t.target_type === 'team') return teamId !== null && parseInt(t.target_value) === teamId;
        return false;
      });
      if (!inTargets) return false;
      return true;
    }

    if (inst.firm_id === null) return false;
    if (firmIds === null) return true;
    return firmIds.includes(inst.firm_id);
  });
};

export const useAlertBadge = () => {
  const { user } = useAuth();
  const { firmIds, loading: firmsLoading } = useAccessibleFirmIds();
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  const fetchCount = useCallback(async () => {
    if (!user?.id || !user?.companyId || firmsLoading) return;
    try {
      const [instResult, cfgResult, dismissResult] = await Promise.all([
        hasuraClient.query(GET_ALERT_INSTANCES, { companyId: parseInt(user.companyId) }),
        hasuraClient.query(GET_ALERT_CONFIGS, {}),
        hasuraClient.query(GET_MY_DISMISSALS, { userId: parseInt(user.id) }),
      ]);
      const all = instResult.alert_instances || [];
      const configs = cfgResult.alert_configs || [];
      const dismissals = dismissResult.alert_dismissals || [];
      const dismissedIds = new Set(dismissals.map(d => d.alert_instance_id));

      const filtered = filterInstances(all, configs, user, firmIds);
      setCount(filtered.filter(i => !dismissedIds.has(i.id)).length);
    } catch {
      // badge için sessiz hata
    }
  }, [user?.id, user?.companyId, user?.role, user?.team_id, firmIds, firmsLoading]);

  useEffect(() => {
    fetchCount();
    timerRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchCount]);

  return { unreadCount: count, refresh: fetchCount };
};
